/* 人生智囊 LifeSage — 外殼負責解析語系，Shell 負責畫面。
   這樣 Shell 內任何地方都能用 useI18n()，而語系本身來自資料庫裡的設定。 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Category, Concept, InboxItem } from "./domain/types.ts";
import type { VersionEntry } from "./domain/oplog.ts";
import { visibleConcepts, deriveTitle, statusCode } from "./domain/rules.ts";
import type { ConceptPatch } from "./data/repository.ts";
import { useLifesage } from "./app/useLifesage.ts";
import { Icon } from "./app/icons.tsx";
import {
  Cabinet, SheetView, Workbench, HistoryPanel, ReviewPanel, SearchPanel,
  TrashPanel, SettingsPanel, Rail, Toast, useToast, useCategoryName,
} from "./app/views.tsx";
import { I18nProvider, useI18n, detectLocale } from "./i18n/index.tsx";
import { useSync } from "./app/useSync.ts";
import { SyncBadge, SyncSection, RecoveryPanel, ConflictPanel } from "./app/syncViews.tsx";
import { ImportDropZone, ImportButton, DraftPanel, ImportProgress } from "./app/importViews.tsx";
import { GraphPanel } from "./app/graphViews.tsx";
import { CategoryPanel } from "./app/categoryViews.tsx";
import type { CategoryDraft } from "./app/categoryViews.tsx";
import type { DropDest } from "./app/importViews.tsx";
import { importFile } from "./import/pipeline.ts";
import { newId } from "./domain/id.ts";
import { buildPortableFiles, exportFileName } from "./export/portable.ts";
import { writeZip } from "./export/zipWrite.ts";
import { downloadBytes } from "./app/download.ts";
import { Tray } from "./app/inboxViews.tsx";
import { visibleInbox } from "./domain/rules.ts";
import { runSelfTest } from "./dev/selftest.ts";

type View = "topic" | "create" | "tray";
type Panel = null | "search" | "history" | "review" | "trash" | "settings" | "conflicts" | "drafts" | "graph"
  | "categories";

export function App() {
  const lifesage = useLifesage();
  /* ?lang=en｜zh-Hant 可覆寫語系：方便驗證、也方便分享特定語系的連結。 */
  const override = new URLSearchParams(location.search).get("lang");
  const locale = detectLocale(
    override === "en" || override === "zh-Hant" ? override : lifesage.state.settings.locale);
  return (
    <I18nProvider locale={locale}>
      <Shell {...lifesage} />
    </I18nProvider>
  );
}

function Shell(props: ReturnType<typeof useLifesage>) {
  const { state, due, search, actions } = props;
  const { t, tn } = useI18n();
  const nameOf = useCategoryName();
  const [view, setView] = useState<View>("topic");
  const [panel, setPanel] = useState<Panel>(null);
  const [catId, setCatId] = useState("");
  /* 副類別是抽屜裡的隔板：空字串＝看整個抽屜。 */
  const [subFilter, setSubFilter] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [reviewIdx, setReviewIdx] = useState(0);
  const [toastMsg, showToast] = useToast();
  const [selfTest, setSelfTest] = useState<string[] | null>(null);
  const [importing, setImporting] = useState({ current: 0, total: 0, fileName: "" });
  const [pendingInbox, setPendingInbox] = useState<InboxItem | null>(null);
  const seeding = useRef(false);

  useEffect(() => {
    if (location.search.includes("selftest=1")) void runSelfTest().then(setSelfTest);
  }, []);

  const sync = useSync(props.repo, state.settings.deviceId, props.reload);
  const conflicts = useMemo(() => state.concepts.filter((c) => c.conflictOf), [state.concepts]);
  /* 草稿＝待補採集標籤。衝突副本也是草稿，但它有自己的一個面板，所以這裡排除。 */
  const drafts = useMemo(
    () => state.concepts.filter((c) => c.status === "draft" && !c.conflictOf), [state.concepts]);

  /** 拖曳或選檔進來：解析 → 指紋去重 → 產生草稿。原檔不落地。 */
  const handleFiles = useCallback(async (files: File[], dest: DropDest) => {
    if (!props.repo) return;
    /* 托盤：素材本體存進 IndexedDB，蓋著硫酸紙等你有想法。
       未掀開之前不建立任何 object URL —— 圖片內容在記憶體裡根本不會被解碼。 */
    if (dest === "tray") {
      for (const file of files) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const id = "inbox-" + newId();
        await props.repo.putBlob(id, bytes, file.type || "application/octet-stream");
        await props.repo.saveInbox({
          id, kind: file.type.startsWith("image/") ? "image" : "file",
          name: file.name, mime: file.type || "", bytes: bytes.byteLength,
          peeled: false, sensitive: false, blobRef: id, createdAt: Date.now(), movedToConceptId: null,
        });
      }
      await props.reload();
      showToast(files.length + " " + t("tray.packs"));
      setView("tray");
      return;
    }
    let imported = 0, duplicates = 0, failed = 0, suggested = "";
    for (let i = 0; i < files.length; i++) {
      const file = files[i]!;
      setImporting({ current: i + 1, total: files.length, fileName: file.name });
      const res = await importFile(props.repo, file, {
        categories: state.categories, concepts: state.concepts,
        adultUnlocked: state.settings.adultUnlocked,
      });
      if (res.status === "imported") {
        imported += res.concepts;
        if (res.suggestion) {
          const cat = state.categories.find((c) => c.id === res.suggestion!.categoryId);
          if (cat) suggested = cat.name;
        }
      } else if (res.status === "duplicate") duplicates++;
      else failed++;
      if (res.status !== "imported" && res.error) showToast(res.error);
    }
    setImporting({ current: 0, total: 0, fileName: "" });
    await props.reload();
    if (imported) {
      showToast(t("import.imported", { n: imported, file: files[0]!.name })
        + (suggested ? "　" + t("import.suggested", { name: suggested }) : ""));
      setPanel("drafts");
    } else if (duplicates) {
      showToast(t("import.duplicate", { file: files[0]!.name }));
    }
  }, [props.repo, props.reload, state.categories, state.concepts, state.settings.adultUnlocked, showToast, t]);

  /** 匯出全部：Markdown ＋ JSON ＋ 托盤素材，打包成一個 zip。
      這是「離開這個 App 也拿得走」的保證 —— 也是使用者敢把十年資料放進來的理由。 */
  const exportEverything = useCallback(async () => {
    if (!props.repo) return;
    const attachments: { id: string; name: string; mime: string; bytes: Uint8Array }[] = [];
    for (const item of state.inbox) {
      const blob = await props.repo.getBlob(item.id);
      if (blob) attachments.push({ id: item.id, name: item.name, mime: blob.mime, bytes: blob.bytes });
    }
    const files = buildPortableFiles({
      concepts: state.concepts, categories: state.categories, links: state.links,
      inbox: state.inbox, attachments,
    });
    downloadBytes(await writeZip(files), exportFileName(), "application/zip");
    showToast(t("export.done", { n: state.concepts.length }));
  }, [props.repo, state.concepts, state.categories, state.links, state.inbox, showToast, t]);

  const loadBlob = useCallback(async (id: string): Promise<Blob | null> => {
    const row = await props.repo?.getBlob(id);
    return row ? new Blob([row.bytes as unknown as BlobPart], { type: row.mime }) : null;
  }, [props.repo]);

  /* 解鎖後自動同步一次（開 App 時自動、之後靠手動按鈕）。 */
  const autoSynced = useRef(false);
  useEffect(() => {
    if (autoSynced.current) return;
    if (!sync.state.unlocked || !sync.state.config?.serverUrl) return;
    autoSynced.current = true;
    void sync.syncNow();
  }, [sync.state.unlocked, sync.state.config?.serverUrl, sync]);

  /* 驗證用種子：只在 ?db=<獨立庫>&seed=1 時執行。ref 鎖避免非同步建立期間重入。 */
  useEffect(() => {
    if (!state.ready || !location.search.includes("seed=1") || state.concepts.length > 0) return;
    if (seeding.current) return;
    seeding.current = true;
    const pick = (n: string) => state.categories.find((c) => c.name === n);
    const bio = pick("生命科學"), cs = pick("資訊與程式"), math = pick("數學");
    if (!bio || !cs || !math) return;
    const DAY = 86_400_000;
    void (async () => {
      await actions.createConcept({ summary: "粒線體是細胞的能量工廠，擁有自己獨立的 DNA。",
        body: "內共生起源：粒線體原本是自由生活的細菌，被真核細胞吞入後成為共生體。",
        note: "高中生物只說它是發電廠，這裡補上內共生起源與母系遺傳的原因。",
        source: "分子生物學 第 7 章", primaryCategoryId: bio.id, reviewDueAt: Date.now() - 3 * DAY });
      await actions.createConcept({ summary: "快取失效比命名難，因為兩者都取決於你還沒見過的未來。",
        note: "我在寫同步層時踩過一次：以為 TTL 夠用，離線三天的裝置回來拿到舊資料。",
        source: "專案筆記 2026-08", primaryCategoryId: cs.id, reviewDueAt: Date.now() - 10 * DAY });
      await actions.createConcept({ summary: "大 O 記號描述成長趨勢，不是實際執行時間。",
        note: "用來判斷圖譜要一次畫幾個節點：O(n²) 的邊在手機上會直接炸掉。",
        source: "演算法導論 第 3 章", primaryCategoryId: math.id });
      await actions.createConcept({ summary: "成人區示範：性癖的形成與演化。",
        note: "示範用的成人知識，未解鎖時不該出現在搜尋、計數與匯出的任何地方。",
        source: "示範資料", nsfw: true, primaryCategoryId: bio.id });
      await actions.createConcept({ summary: "尚未補齊採集標籤的草稿。", note: "",
        source: "未命名.pdf", status: "draft", primaryCategoryId: bio.id });
    })();
  }, [state.ready, state.concepts.length, state.categories, actions]);

  useEffect(() => {
    if (catId || state.categories.length === 0) return;
    const bio = state.categories.find((c) => c.seedKey === "life") ?? state.categories[0]!;
    setCatId(bio.id);
  }, [state.categories, catId]);

  useEffect(() => {
    document.body.setAttribute("data-material", state.settings.sheetMaterial);
    document.documentElement.style.setProperty("--fs", String(state.settings.typeScale));
  }, [state.settings]);

  const shown = useMemo(
    () => visibleConcepts(state.concepts, { adultUnlocked: state.settings.adultUnlocked, includeDraft: true }),
    [state.concepts, state.settings.adultUnlocked]);
  const inDrawer = useMemo(() => shown.filter((c) => (subFilter
    ? c.subCategoryIds.includes(subFilter) : c.primaryCategoryId === catId)), [shown, catId, subFilter]);
  const current = inDrawer.find((c) => c.id === selectedId) ?? inDrawer[0] ?? null;
  const trashed = useMemo(() => state.concepts.filter((c) => c.status === "trashed"), [state.concepts]);
  const results = useMemo(() => (query.trim() ? search(query) : []), [query, search]);
  const catName = nameOf(state.categories.find((c) => c.id === catId));
  /* 目前抽屜底下的隔板（副類別）。沒有隔板就不顯示那一排。 */
  const subs = useMemo(
    () => state.categories.filter((c) => c.parentId === catId), [state.categories, catId]);

  const openHistory = async (c: Concept) => { setVersions(await actions.historyOf(c)); setPanel("history"); };

  const saveCategoryDraft = async (d: CategoryDraft) => {
    const isNew = !d.id;
    await actions.saveCategory({
      id: d.id, parentId: d.parentId, name: d.name, definition: d.definition,
      reviewDefaultDays: d.reviewDefaultDays, linkCapPerNode: d.linkCapPerNode,
    });
    showToast(isNew ? t("cat.created", { name: d.name }) : t("cat.saved", { name: d.name }));
  };

  const removeCategory = async (c: Category) => {
    /* 刪掉的正好是目前打開的抽屜（或它的隔板）時，退回一個還存在的抽屜 ——
       否則畫面會停在一個空的抽屜上，看起來像整個資料庫被清掉了。 */
    const wasOpen = catId === c.id
      || (subFilter !== "" && (subFilter === c.id
        || state.categories.some((x) => x.id === subFilter && x.parentId === c.id)));
    const moved = await actions.deleteCategory(c.id);
    if (wasOpen) { setCatId(""); setSubFilter(""); setSelectedId(""); }
    showToast(moved > 0 ? t("cat.deleted.moved", { n: moved }) : t("cat.deleted", { name: c.name }));
  };
  const handleCreate = async (patch: ConceptPatch) => {
    const c = await actions.createConcept(patch);
    /* 從托盤壓製過來的素材：記下它變成了哪條知識，然後清掉本體（原檔不落地）。 */
    if (pendingInbox && props.repo) {
      await props.repo.saveInbox({ ...pendingInbox, movedToConceptId: c.id });
      await props.repo.deleteBlob(pendingInbox.id);
      setPendingInbox(null);
    }
    setCatId(c.primaryCategoryId); setSubFilter(""); setSelectedId(c.id); setView("topic");
    showToast(patch.status === "draft" ? t("toast.draft") : t("toast.created"));
  };

  return (
    <div className="app">
      <header className="topbar">
        <span className="brand">{t("app.name")}</span>
        <span className="mono dim">{state.ready ? tn("topbar.count", state.concepts.length) : t("topbar.loading")}</span>
        <span style={{ flex: 1 }} />
        <button className="iconbtn" aria-label={t("topbar.search")} onClick={() => setPanel("search")}>
          <Icon name="search" /></button>
        <button className="iconbtn" aria-label={t("topbar.adult")} aria-pressed={state.settings.adultUnlocked}
          onClick={() => void actions.saveSettings({ adultUnlocked: !state.settings.adultUnlocked })}>
          <Icon name={state.settings.adultUnlocked ? "unlock" : "lock"} /></button>
        {drafts.length > 0 && (
          <button className="iconbtn syncbadge" aria-label={t("import.drafts.title")} onClick={() => setPanel("drafts")}
            title={t("import.drafts.count", { n: drafts.length })}>
            <Icon name="peel" /><span className="badge mono">{drafts.length}</span>
          </button>)}
        <SyncBadge sync={sync} onOpen={() => setPanel("settings")} />
        {conflicts.length > 0 && (
          <button className="iconbtn" aria-label={t("sync.conflicts.title")} onClick={() => setPanel("conflicts")}
            title={t("sync.conflicts.title")}><Icon name="warn" /></button>)}
        <button className="iconbtn" aria-label={t("topbar.trash")} onClick={() => setPanel("trash")}>
          <Icon name="trash" /></button>
        <button className="iconbtn" aria-label={t("topbar.settings")} onClick={() => setPanel("settings")}>
          <Icon name="more" /></button>
      </header>

      {due.length > 0 && (
        <div className="banner">
          <span className="bgroup"><span className="mono">{due.length}</span><span>{t("banner.due")}</span></span>
          <span style={{ flex: 1 }} />
          <button onClick={() => { setReviewIdx(0); setPanel("review"); }}>{t("banner.review")}</button>
        </div>
      )}

      <ImportProgress current={importing.current} total={importing.total} fileName={importing.fileName} />
      {state.error && (
        <div className="notice warn">
          <Icon name="warn" /><span>{t("error.storage", { message: state.error })}</span>
        </div>
      )}
      {!state.ready && <div className="notice"><span>{t("loading.cabinet")}</span></div>}

      <Cabinet categories={state.categories} concepts={shown} catId={catId}
        onPick={(id) => { setCatId(id); setSubFilter(""); setSelectedId(""); setView("topic"); }}
        onManage={() => setPanel("categories")} />

      {subs.length > 0 && (
        <div className="subchips" data-subchips>
          <button className="chip" aria-pressed={subFilter === ""} data-sub=""
            onClick={() => { setSubFilter(""); setSelectedId(""); }}>{t("cat.all")}</button>
          {subs.map((s) => (
            <button key={s.id} className="chip" aria-pressed={subFilter === s.id} data-sub={s.id}
              onClick={() => { setSubFilter(s.id); setSelectedId(""); }}>{nameOf(s)}</button>
          ))}
        </div>
      )}

      <main className="sheetwrap">
        {view === "topic" && !current && state.ready && (
          <section className="panel">
            <h2 style={{ display: "block" }}>{t("empty.title")}</h2>
            <p className="dim">{t("empty.body")}</p>
            <div className="row" style={{ marginTop: 16 }}>
              <button className="btn primary" onClick={() => setView("create")}>{t("empty.action")}</button>
            </div>
          </section>
        )}
        {view === "topic" && current && (
          <SheetView concept={current} categories={state.categories} links={state.links}
            allConcepts={visibleConcepts(state.concepts, { adultUnlocked: state.settings.adultUnlocked,
              includeDraft: true })}
            onSave={async (patch) => { await actions.updateConcept(current, patch); showToast(t("toast.saved")); }}
            onTrash={async () => {
              await actions.setStatus(current, "trashed"); setSelectedId(""); showToast(t("toast.trashed")); }}
            onHistory={() => void openHistory(current)}
            onGraph={() => setPanel("graph")}
            onCreateLink={async (toId, type, note) => {
              await actions.createLink(current.id, toId, type, note);
              showToast(t("link.created"));
            }}
            onDeleteLink={async (id) => { await actions.deleteLink(id); showToast(t("link.delete")); }} />
        )}
        {view === "create" && (
          <>
            <div className="row" style={{ maxWidth: "70ch", margin: "0 auto 16px" }}>
              <ImportButton dest="work" onFiles={(f, d) => void handleFiles(f, d)} />
              <span className="hint" style={{ margin: 0 }}>{t("import.drop.workHint")}</span>
            </div>
            <Workbench categories={state.categories} defaultCategoryId={catId}
              initialSource={pendingInbox?.name}
              onCreate={(patch) => void handleCreate(patch)} />
          </>
        )}
        {view === "tray" && (
          <Tray items={visibleInbox(state.inbox, state.settings.adultUnlocked)}
            hiddenCount={state.inbox.length - visibleInbox(state.inbox, state.settings.adultUnlocked).length}
            adultUnlocked={state.settings.adultUnlocked} load={loadBlob}
            onPickFiles={() => {
              const input = document.querySelector("[data-import-input]") as HTMLInputElement | null;
              if (input) { input.dataset["dest"] = "tray"; input.click(); }
            }}
            onPeel={async (item) => {
              await props.repo?.saveInbox({ ...item, peeled: !item.peeled });
              await props.reload();
              if (!item.peeled) showToast(t("tray.peeled"));
            }}
            onToggleSensitive={async (item) => {
              const next = !item.sensitive;
              await props.repo?.saveInbox({ ...item, sensitive: next });
              await props.reload();
              /* 標記敏感之後它會立刻從畫面上消失（成人區鎖定時的正確行為）。
                 不解釋的話，使用者會以為東西被刪掉了。 */
              if (next && !state.settings.adultUnlocked) showToast(t("tray.marked"));
              else showToast(next ? t("tray.sensitive") : t("tray.unmarked"));
            }}
            onPress={(item) => { setPendingInbox(item); setView("create"); showToast(t("tray.pressed")); }}
            onDelete={async (item) => {
              await props.repo?.deleteInbox(item.id);
              await props.repo?.deleteBlob(item.id);
              await props.reload();
              showToast(t("tray.deleted"));
            }}
            onGoCreate={() => setView("create")} />
        )}
        {selfTest && (
          <section className="panel" style={{ marginTop: 24 }}>
            <h2 style={{ display: "block" }}>{t("selftest.title")}</h2>
            <pre id="selftest" className="mono" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
              {selfTest.join("\n")}</pre>
          </section>
        )}
      </main>

      <Rail concept={current} links={state.links} due={due.length} inbox={state.inbox.length}
        links_total={state.links.length} onGraph={() => setPanel("graph")}
        catName={catName} onReview={() => { setReviewIdx(0); setPanel("review"); }}
        counts={[
          { label: t("rail.specimens"), value: tn("rail.specimens.value", state.concepts.filter((c) => c.status === "active").length) },
          { label: t("rail.drafts"), value: tn("rail.specimens.value", state.concepts.filter((c) => c.status === "draft").length) },
          { label: t("rail.trash"), value: tn("rail.specimens.value", trashed.length) },
          { label: t("rail.linkCap"), value: t("rail.linkCap.value") },
        ]} />

      <nav className="nav" aria-label={t("settings.title")}>
        {([["topic", "nav.topic", "nav.topic.sub"], ["create", "nav.create", "nav.create.sub"],
           ["tray", "nav.tray", "nav.tray.sub"]] as const).map(([id, label, sub]) => (
          <button key={id} aria-current={view === id ? "page" : undefined}
            aria-label={t(label) + "・" + t(sub)} onClick={() => setView(id)}>
            <span className="n">{t(label)}</span><span className="sub">{t(sub)}</span>
          </button>))}
      </nav>

      {panel === "search" && (
        <SearchPanel query={query} results={results} adultUnlocked={state.settings.adultUnlocked}
          onQuery={setQuery}
          onPick={(c) => { setCatId(c.primaryCategoryId); setSelectedId(c.id); setView("topic"); setPanel(null); }}
          onClose={() => setPanel(null)} />)}
      {panel === "history" && current && (
        <HistoryPanel concept={current} versions={versions}
          onRestore={async (rev) => {
            const c = await actions.restoreVersion(current, rev);
            setSelectedId(c.id); setVersions(await actions.historyOf(c));
            showToast(t("toast.restored", { n: rev }));
          }}
          onClose={() => setPanel(null)} />)}
      {panel === "review" && (
        <ReviewPanel due={due} index={Math.min(reviewIdx, Math.max(0, due.length - 1))}
          onKeep={async () => { const c = due[reviewIdx];
            if (c) { await actions.reviewKeep(c); showToast(t("toast.kept")); setReviewIdx((i) => i + 1); } }}
          onEdit={() => { const c = due[reviewIdx];
            if (c) { setCatId(c.primaryCategoryId); setSelectedId(c.id); setView("topic"); setPanel(null); } }}
          onTrash={async () => { const c = due[reviewIdx];
            if (c) { await actions.setStatus(c, "trashed"); showToast(t("toast.dropped")); } }}
          onClose={() => setPanel(null)} />)}
      {panel === "trash" && (
        <TrashPanel items={trashed}
          onRestore={async (c) => { await actions.setStatus(c, "active");
            showToast(t("toast.undeleted", { title: deriveTitle(c.summary) })); }}
          onPurge={async () => { const n = await actions.emptyTrash(); showToast(tn("toast.emptied", n)); }}
          onClose={() => setPanel(null)} />)}
      {panel === "settings" && (
        <SettingsPanel settings={state.settings} onSave={(p) => void actions.saveSettings(p)}
          onClose={() => setPanel(null)}>
          <SyncSection sync={sync} />
          <div className="panel">
            <h2 style={{ display: "block" }}>{t("export.title")}</h2>
            <p className="hint">{t("export.hint")}</p>
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn primary" data-export-all onClick={() => void exportEverything()}>
                {t("export.button")}</button>
            </div>
          </div>
        </SettingsPanel>)}
      {sync.state.recoveryCode && (
        <RecoveryPanel code={sync.state.recoveryCode} onConfirm={sync.dismissRecovery} />)}
      {panel === "conflicts" && (
        <ConflictPanel copies={conflicts} originals={(id) => state.concepts.find((c) => c.id === id)}
          onTake={async (copy) => {
            const original = state.concepts.find((c) => c.id === copy.conflictOf);
            if (original) {
              await actions.updateConcept(original, {
                title: copy.title, summary: copy.summary, body: copy.body, note: copy.note,
                source: copy.source, primaryCategoryId: copy.primaryCategoryId,
                reviewDueAt: copy.reviewDueAt, reviewIntervalDays: copy.reviewIntervalDays, nsfw: copy.nsfw });
            }
            await actions.setStatus(copy, "trashed");
            showToast(t("sync.conflicts.toast.take"));
          }}
          onDiscard={async (copy) => { await actions.setStatus(copy, "trashed"); showToast(t("sync.conflicts.toast.discard")); }}
          onKeepBoth={async (copy) => {
            await actions.updateConcept(copy, { conflictOf: null, status: "active" });
            showToast(t("sync.conflicts.toast.keepBoth"));
          }}
          onClose={() => setPanel(null)} />)}

      {panel === "categories" && (
        <CategoryPanel categories={state.categories} concepts={shown}
          onSave={saveCategoryDraft} onDelete={removeCategory}
          onClose={() => setPanel(null)} />)}
      {panel === "graph" && (
        <GraphPanel concepts={visibleConcepts(state.concepts, { adultUnlocked: state.settings.adultUnlocked })}
          links={state.links} categories={state.categories}
          onExported={(n) => { setPanel(null); showToast(t("graph.exported", { n })); }}
          onClose={() => setPanel(null)} />)}
      {panel === "drafts" && (
        <DraftPanel drafts={drafts} categories={state.categories}
          onComplete={async (draft, note) => {
            await actions.updateConcept(draft, { note, status: "active" });
            showToast(t("import.drafts.done", { title: deriveTitle(draft.summary) }));
          }}
          onDiscard={async (draft) => { await actions.setStatus(draft, "trashed");
            showToast(t("toast.trashed")); }}
          onOpen={(draft) => {
            setCatId(draft.primaryCategoryId); setSelectedId(draft.id); setView("topic"); setPanel(null);
          }}
          onClose={() => setPanel(null)} />)}
      <ImportDropZone onFiles={(files, dest) => void handleFiles(files, dest)} />
      <Toast message={toastMsg} />
      <span className="sr" data-status={current ? t(statusCode(current.status)) : "none"} />
    </div>
  );
}
